import { useEffect, useState } from "react";

import { Icons } from "../lib/icons";
import { Mark } from "../lib/logo";

export type View =
  | "pulpit" | "procesy" | "schemat" | "marki" | "audyt" | "sms" | "api" | "dostepy" | "webhooki";

interface NavItem {
  id: View;
  label: string;
  icon: keyof typeof Icons;
  badge?: number;
  /** Plakietka „nowe" przy świeżym dziale — zamiast licznika. */
  fresh?: boolean;
}

/**
 * Szkielet panelu: stała kolumna nawigacji po lewej, treść po prawej.
 *
 * Menu boczne zamiast paska na górze, bo pozycji jest kilka, a każda ma swój
 * licznik — pionowa lista mieści etykietę, ikonę i liczbę bez ściskania.
 * Kolumnę da się zwinąć do samych ikon, gdy tabela potrzebuje szerokości.
 */
export function Shell({
  view,
  onView,
  counts,
  children,
  onSearch,
  canManageUsers = false,
}: {
  view: View;
  onView: (view: View) => void;
  counts: { procesy?: number; schemat?: number };
  children: React.ReactNode;
  onSearch: (query: string) => void;
  /** Zarządzanie dostępami widzi tylko superadministrator. */
  canManageUsers?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [query, setQuery] = useState("");

  // Motyw mieszka w Shellu, nie w przełączniku: karta logo zabrania stawiać
  // czerwony znak na ciemnym tle, więc wariant sygnetu musi wiedzieć o motywie —
  // a sygnet renderuje Shell. Cała mechanika (localStorage, atrybut na <html>,
  // zdjęcie przy odmontowaniu) jedzie bez zmian, tylko piętro wyżej.
  const [dark, setDark] = useState(() => {
    try {
      return window.localStorage.getItem(THEME_KEY) === "dark";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (dark) document.documentElement.setAttribute("data-theme", "dark");
    else document.documentElement.removeAttribute("data-theme");
    try {
      window.localStorage.setItem(THEME_KEY, dark ? "dark" : "light");
    } catch {
      /* prywatne okno bez localStorage — motyw po prostu nie przeżyje odświeżenia */
    }
  }, [dark]);

  // Wyjście z panelu (np. „Podgląd procesu") nie może zostawić kandydatowi
  // ciemnego ekranu — atrybut schodzi razem z Shellem.
  useEffect(() => () => document.documentElement.removeAttribute("data-theme"), []);

  // ⌘K / Ctrl+K — skrót, którego szuka się odruchowo w każdym panelu.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        document.getElementById("adm-search")?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const main: NavItem[] = [
    { id: "pulpit", label: "Pulpit", icon: "spark" },
    { id: "procesy", label: "Procesy", icon: "user", badge: counts.procesy },
    // Projektowanie procesu to inna praca niż prowadzenie spraw: jeden zły
    // warunek w grafie dotyka wszystkich kandydatów naraz. Konsultant go
    // nie widzi, bo i serwer go tam nie wpuści.
    ...(canManageUsers
      ? [{ id: "schemat" as const, label: "Schemat", icon: "document" as const, badge: counts.schemat }]
      : []),
    // Szablon zmienia to, co widzi każdy kandydat procesu — ta sama waga
    // co edycja schematu, więc ta sama rola.
    ...(canManageUsers
      ? [{ id: "marki" as const, label: "Szablony marki", icon: "spark" as const, fresh: true }]
      : []),
  ];
  const tools: NavItem[] = [
    { id: "audyt", label: "Audyt", icon: "shield" },
    { id: "sms", label: "Wiadomości", icon: "clock" },
    // Dostępy widzi tylko ten, kto nimi zarządza. Konsultantowi pokazywalibyśmy
    // ekran, który i tak odpowie „brak uprawnień".
    ...(canManageUsers ? [{ id: "dostepy" as const, label: "Dostępy", icon: "shield" as const }] : []),
    // Webhook wysyła identyfikatory spraw pod zewnętrzny adres, więc zarządza
    // nimi ten sam człowiek, który zarządza dostępami.
    ...(canManageUsers ? [{ id: "webhooki" as const, label: "Webhooki", icon: "globe" as const }] : []),
    // Integrator dostaje klucz do panelu, nie dostęp do repozytorium —
    // dokumentacja API musi być tam, gdzie on jest.
    { id: "api", label: "Dokumentacja API", icon: "document" },
  ];

  const renderItem = (item: NavItem) => {
    // Zbiór ikon procesu kandydata ma za mało kształtów na panel: „Audyt"
    // i „Dostępy" dzieliły tarczę, „Schemat" i „Dokumentacja API" kartkę —
    // a ikona, która znaczy dwie rzeczy, przestaje znaczyć cokolwiek.
    // Lokalne kształty (jak słońce i księżyc niżej) rozplatają duplikaty
    // bez dopisywania ikon do zbioru kandydata.
    const Icon = NAV_ICON[item.id] ?? Icons[item.icon];
    return (
      <button
        key={item.id}
        className={`side__link${view === item.id ? " side__link--on" : ""}`}
        onClick={() => onView(item.id)}
        title={collapsed ? item.label : undefined}
      >
        <Icon size={18} />
        {!collapsed && (
          <>
            <span className="side__label">{item.label}</span>
            {item.badge != null && <span className="side__badge">{item.badge}</span>}
            {item.fresh && <span className="side__new">nowe</span>}
          </>
        )}
      </button>
    );
  };

  return (
    <div className={`adm${collapsed ? " adm--tight" : ""}`}>
      <aside className="side">
        <div className="side__brand">
          {/* Na ciemnym tle czerwony znak jest zakazany kartą logo — idzie
              wariant `reverse` (biały kafel). */}
          <Mark size={20} wariant={dark ? "reverse" : "primary"} />
          {!collapsed && (
            <span className="side__brandtext">
              <strong className="logo__slowo">for apps</strong>
              <small>Panel konsultanta</small>
            </span>
          )}
          <button
            className="side__collapse"
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? "Rozwiń menu" : "Zwiń menu"}
          >
            {collapsed ? "»" : "«"}
          </button>
        </div>

        {!collapsed && (
          <div className="side__search">
            {/* Lupa, nie globus — pole wyszukiwania ma wyglądać jak wyszukiwanie. */}
            <SearchIcon />
            <input
              id="adm-search"
              type="search"
              placeholder="Szukaj kandydata"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                onSearch(event.target.value);
                if (event.target.value) onView("procesy");
              }}
            />
            <kbd>⌘K</kbd>
          </div>
        )}

        <nav className="side__nav">
          {!collapsed && <p className="side__section">Główne</p>}
          {main.map(renderItem)}
          {!collapsed && <p className="side__section">Narzędzia</p>}
          {tools.map(renderItem)}
        </nav>

        <span className="side__spacer" />

        <div className="side__foot">
          <ThemeSwitch dark={dark} onChange={setDark} />
          <a className="side__link" href="/" title="Podgląd procesu">
            <Icons.plane size={18} />
            {!collapsed && <span className="side__label">Podgląd procesu</span>}
          </a>
        </div>
      </aside>

      <div className="adm__pane">{children}</div>
    </div>
  );
}

/** Klucz w localStorage — sam wybór motywu, nic więcej tam nie jedzie. */
const THEME_KEY = "panel-motyw";

/**
 * Przełącznik motywu jasny/ciemny — sama prezentacja.
 *
 * Ciemny motyw dotyczy wyłącznie panelu: proces kandydata bywa drukowany
 * i oglądany w słońcu. Stan i skutki uboczne (localStorage, atrybut
 * `data-theme` na <html>, zdjęcie go przy odmontowaniu) trzyma Shell,
 * bo od motywu zależy też wariant sygnetu w nagłówku menu.
 */
function ThemeSwitch({ dark, onChange }: { dark: boolean; onChange: (dark: boolean) => void }) {
  return (
    <div className="theme" role="group" aria-label="Motyw panelu">
      <button
        type="button"
        className={`theme__opt${!dark ? " theme__opt--on" : ""}`}
        aria-pressed={!dark}
        aria-label="Motyw jasny"
        title="Motyw jasny"
        onClick={() => onChange(false)}
      >
        <SunIcon />
      </button>
      <button
        type="button"
        className={`theme__opt${dark ? " theme__opt--on" : ""}`}
        aria-pressed={dark}
        aria-label="Motyw ciemny"
        title="Motyw ciemny"
        onClick={() => onChange(true)}
      >
        <MoonIcon />
      </button>
    </div>
  );
}

/* Słońce i księżyc lokalnie, nie w lib/icons.tsx: tamten zbiór należy do kart
   wyboru kandydata i rozwija go proces publiczny — panel nie dopisuje mu ikon.
   Z tego samego powodu lokalne są też kształty menu (lupa, graf, dymek, klucz):
   kreska 1.8 i zaokrąglenia jak w zbiorze kandydata, żeby siedziały w jednym
   rzędzie z jego ikonami bez zgrzytu. */

const NAV_ICON: Partial<Record<View, () => React.JSX.Element>> = {
  schemat: GraphIcon,
  marki: SwatchIcon,
  sms: ChatIcon,
  dostepy: KeyIcon,
};

function SearchIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="10.5" cy="10.5" r="6.2" />
        <path d="m15.3 15.3 5 5" />
      </g>
    </svg>
  );
}

/** Schemat: trzy węzły i dwie krawędzie — graf procesu w jednym spojrzeniu. */
function GraphIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="12" cy="5.2" r="2.5" />
        <circle cx="5.5" cy="18.5" r="2.5" />
        <circle cx="18.5" cy="18.5" r="2.5" />
        <path d="M10.9 7.4 6.8 16.3M13.1 7.4l4.1 8.9" />
      </g>
    </svg>
  );
}

/** Szablony marki: cztery próbki koloru — paleta, nie dokument. */
function SwatchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
        <rect x="3.5" y="3.5" width="7" height="7" rx="2" />
        <rect x="13.5" y="3.5" width="7" height="7" rx="3.5" />
        <rect x="3.5" y="13.5" width="7" height="7" rx="3.5" />
        <rect x="13.5" y="13.5" width="7" height="7" rx="2" />
      </g>
    </svg>
  );
}

/** Wiadomości: dymek rozmowy — zegar znaczył tu „historię", nie „wysyłkę". */
function ChatIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M4.5 7a3 3 0 0 1 3-3h9a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H9.8l-4.4 3.6a.5.5 0 0 1-.9-.4V7Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Dostępy: klucz — tarcza zostaje audytowi, który naprawdę pilnuje reguł. */
function KeyIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="8" cy="12" r="3.4" />
        <path d="M11.4 12h9M17 12v3.2M20.4 12v2.4" />
      </g>
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="12" cy="12" r="4.2" />
        <path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7" />
      </g>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M20 14.5A8.5 8.5 0 0 1 9.5 4 8.5 8.5 0 1 0 20 14.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Nagłówek strony: tytuł, podtytuł i akcje po prawej. */
export function PageHead({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <header className="page__head">
      <div>
        <h1 className="page__title">{title}</h1>
        {subtitle && <p className="page__subtitle">{subtitle}</p>}
      </div>
      <span className="adm__spacer" />
      {actions}
    </header>
  );
}
